import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// bg-card, not bg-white: --card is 0 0% 100% in light, so this is pixel-identical there, and 0 0% 10% in dark, where a hardcoded white left near-white text on a white pill.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary - Purple CTA (main action buttons)
        default: "bg-accent text-white hover:bg-accent/90 shadow-sm font-semibold",
        // Destructive - Red for dangerous actions
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm",
        // Outline - White with border (secondary actions)
        outline: "border border-border bg-card text-foreground hover:bg-muted hover:text-foreground",
        // Secondary - Light background
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        // Ghost - Minimal (icon buttons, subtle actions)
        ghost: "hover:bg-muted hover:text-foreground",
        // Link - Text only
        link: "text-primary underline-offset-4 hover:underline",
        // Accent - Gradient dark purple button for emphasis
        accent: "gradient-button text-white font-semibold hover:opacity-90 shadow-md hover:shadow-lg",
        // Success - Green
        success: "bg-success text-success-foreground hover:bg-success/90 shadow-sm",
        // Sidebar variants
        sidebar: "bg-sidebar-muted/50 text-sidebar-foreground hover:bg-sidebar-muted/80",
        sidebarActive: "bg-sidebar-accent text-sidebar-accent-foreground font-semibold",
        // Purple primary variant
        primary: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
        // VA-141 — the prototype's two buttons, taken from its stylesheet rather
        // than by eye. `.cta` is gold with #3a2600 ink and a gold glow; `.btn-out`
        // is white with a pink hairline. Added as variants rather than changed in
        // place: this ticket builds the set and adopts it nowhere, so no existing
        // screen may change appearance.
        cta: "bg-brand-gold text-brand-gold-ink font-bold rounded-[10px] shadow-[0_8px_16px_-8px_hsl(var(--brand-gold)/0.7)] hover:bg-brand-gold/90",
        outlinePink: "border border-brand-pink bg-card text-foreground font-semibold rounded-[9px] hover:bg-brand-pink/5",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3 text-sm",
        lg: "h-11 rounded-lg px-6 text-base",
        xl: "h-12 rounded-lg px-8 text-base font-semibold",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
